import { createAction } from 'redux-actions';
import { compose, path } from 'ramda';
import Cookie from 'js-cookie';
import { replace } from 'connected-react-router';

import {
  TRANSLATIONS_FETCHED,
  LOCALE_CHANGED,
  LANGUAGE_SELECTED,
} from 'app/features/localization/actionTypes';
import LocaleInterceptor from 'app/api/interceptors/LocaleInterceptor';
import { updateThemeMarkup } from 'app/styles/actions';
import { getLocales } from 'app/features/localization/selectors';
import {
  getLocationHref,
  getPathnameWithoutLocale,
} from 'app/router/selectors';
import {
  DEFAULT_LOCALE,
  LOCALE_COOKIE,
} from 'app/features/localization/constants';
import { fetchPlans } from 'app/features/plans/actions';
import domainFromHostname from 'app/router/helpers/domainFromHostname';
import translations from 'app/features/localization/translations.json';
import localizeLink from 'app/features/localization/localizeLink';

const localeChanged = createAction(LOCALE_CHANGED);
const localizationLanguageSelected = createAction(LANGUAGE_SELECTED);

const fetchTranslationsDataResponse = createAction(TRANSLATIONS_FETCHED);

function setLocaleCookie(locale) {
  const domain = domainFromHostname();

  Cookie.set(LOCALE_COOKIE, locale, { domain, expires: 365 });
}

function updateHtmlDirection(locale) {
  return dispatch => {
    const html = path(['document', 'documentElement'], window);

    if (!locale || !html) return;

    const slicedLocale = locale.slice(0, 2);

    if (slicedLocale !== html.lang) {
      const markup = {
        dir: locale === 'arME' ? 'rtl' : 'ltr',
        startDirection: locale === 'arME' ? 'right' : 'left',
        endDirection: locale === 'arME' ? 'left' : 'right',
      };

      html.lang = slicedLocale;
      html.dir = markup.dir;
      dispatch(updateThemeMarkup(markup));
    }
  };
}

function fetchTranslationsData(locale) {
  return (dispatch, _, { api }) =>
    api
      .fetchTranslations({ locale })
      .then(compose(dispatch, fetchTranslationsDataResponse))
      .catch(() => {
        LocaleInterceptor.setLocale(DEFAULT_LOCALE);
        setLocaleCookie(DEFAULT_LOCALE);
        dispatch(localeChanged(DEFAULT_LOCALE));
        dispatch(updateHtmlDirection(DEFAULT_LOCALE));
        dispatch(fetchTranslationsDataResponse(translations));
      });
}

function changeLocale(locale) {
  return dispatch => {
    LocaleInterceptor.setLocale(locale);
    setLocaleCookie(locale);
    dispatch(localeChanged(locale));
    dispatch(fetchTranslationsData(locale));
    dispatch(updateHtmlDirection(locale));
    dispatch(fetchPlans());
  };
}

function validateAndChangeLocale(locale) {
  return (dispatch, getState) => {
    if (getLocales(getState()).includes(locale)) {
      dispatch(changeLocale(locale));
    } else {
      dispatch(changeLocale(DEFAULT_LOCALE));
      dispatch(replace(getPathnameWithoutLocale(getState())));
    }
  };
}

function languageSelected(locale) {
  return (dispatch, getState) => {
    dispatch(validateAndChangeLocale(locale));
    dispatch(localizationLanguageSelected(locale));
    window.location.assign(localizeLink(getLocationHref(getState()), locale));
  };
}

export {
  fetchTranslationsData,
  changeLocale,
  setLocaleCookie,
  updateHtmlDirection,
  validateAndChangeLocale,
  languageSelected,
  localizationLanguageSelected,
};
